import type { GridLayer, Log, PresentationTaskRunner } from "@slot/presentation";
import type { GridPresenter, MechanicPlugin } from "@slot/mechanics-core";
import { MergePresenter } from "./MergePresenter";
import { MergeSymbolsActionHandler } from "./MergeSymbolsActionHandler";
import type { MergeTuning } from "./MergeTuning";
import { SymbolMerge, type MergeLanding } from "./SymbolMerge";

/**
 * The merge as a mechanic plugin, over a removal-and-refill mechanic (decision 47): the base's frames and actions as they
 * are — its reveal, its collapse, its `remove_symbols_by_mask` — plus `merge_symbols`, which merges a substep's clusters
 * on the base's grid and hands the consumed cells to the base's removal, so its next collapse drops into them. The base
 * is a `MechanicPlugin` (the cascade's `buildCascadeReplayPlugin`, the tumble's `buildTumbleReelsReplayPlugin`): the
 * title composes the two, as no mechanic imports another. The slam scopes are the base's: a slam never shortens a merge.
 */
export function buildMergeReplayPlugin(input: {
  base: MechanicPlugin;
  /** The base's grid (the merge moves its symbols). */
  grid: GridLayer;
  /** The base's composite grid presenter (the merge's removal goes through it, the win highlight included). */
  gridPresenter: GridPresenter;
  runner: PresentationTaskRunner;
  tuning: MergeTuning;
  /** The merge cue (the title plays its sound and haptic); omit to play nothing. */
  onMerge?: () => void;
  /** As each target becomes its result (a title's sparkle, its ladder's pulse). */
  onLanding?: (landing: MergeLanding) => void;
  log?: Log;
}): MechanicPlugin {
  const merge = new SymbolMerge({ grid: input.grid, taskRunner: input.runner, tuning: input.tuning, onLanding: input.onLanding });
  const presenter = new MergePresenter({
    grid: input.grid,
    merge,
    gridPresenter: input.gridPresenter,
    runner: input.runner,
    postMergeHoldSeconds: input.tuning.postMergeHoldSeconds,
    onMerge: input.onMerge,
    log: input.log ?? (() => {}),
  });
  const base = input.base.plugin;
  return {
    framePresenter: input.base.framePresenter,
    plugin: {
      name: `merge(${base.name})`,
      actionHandlers: [...(base.actionHandlers ?? []), new MergeSymbolsActionHandler(presenter)],
      observer: base.observer,
      eventHandlers: base.eventHandlers,
    },
    slamScopes: input.base.slamScopes,
  };
}
